import { Bell, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "@/providers/language-provider";
import { useAuth } from "@/providers/auth-provider";
import { useChats } from "@/providers/chat-provider";
import {
  apiGetNotificationCount,
  apiGetNotifications,
  apiReadNotification,
} from "@/lib/client-api";
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useState, useEffect, useCallback } from "react";
import { serviceworkerMessageQueue } from "@/features/service-worker-message";
import { InfiniteScroll } from "@/components/blocks/infinite-scroll";
import { NotificationMessage } from "@/features/notification-message";
import { FRIEND_EVENTS } from "@/pages/subs/friends/friend-manager";
import { CHAT_EVENTS } from "@/providers/chat-provider";
import { MAIN_MENUS, setMainMenu } from "@/pages/layouts/main-header";
import { FRIEND_TABS, setFrendTab } from "@/pages/subs/main-friends";
import { sendMessageToServiceWorker } from "@/features/service-worker-message";

interface Notification {
  id: number;
  userId: string;
  channelId: string;
  title: string;
  body: string;
  data: any;
  createdAt: string;
  expiresAt: string | null;
  isRead: boolean;
}

interface NotificationCount {
  total: number;
  unread: number;
}

interface NotificationPage {
  notifications: Notification[];
  nextPage: number;
  hasMore: boolean;
}

interface NotificationData {
  pages: NotificationPage[];
  pageParams: any[];
}

export function NotificationButton() {
  const { t, language } = useTranslation();
  const { user } = useAuth();
  const { enterChat } = useChats();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    sendMessageToServiceWorker({
      type: "CLIENT_IS_READY",
      timestamp: Date.now(),
    });
  }, []);

  // status === 'pending'  // 초기 로딩 상태
  // status === 'error'    // 에러 발생
  // status === 'success'  // 데이터 로드 성공

  const {
    data: count = { total: 0, unread: 0 },
    //@ts-ignore
    error: countError,
    //@ts-ignore
    status: countStatus, // 'pending', 'error', 'success'
    isFetching: isFetchingCount, // 모든 로딩시 (첫 로딩 + 재요청시)
    //@ts-ignore
    refetch: refetchCount,
  } = useQuery<
    NotificationCount, // queryFn이 반환하는 원본 데이터의 타입
    Error // 에러가 발생했을 때의 타입 (표준 Error 타입 사용)
    // Notification[] // 최종적으로 사용할 데이터의 타입 (select에서 변환된 후의 타입)
  >({
    queryKey: ["notification_count", user?._id],
    queryFn: () => apiGetNotificationCount(user.token),
    // select: (data) => data.notifications, // data.notifications를 직접 반환
    enabled: !!user,
    // placeholderData: keepPreviousData, // 새 데이터를 가져오는 동안 이전 데이터 유지
    // // 30초 동안은 데이터를 fresh하게 유지
    // staleTime: 1000 * 30, // (기본값: 0)
    // // 5분마다 자동 갱신
    // refetchInterval: 5 * 60 * 1000,
    // // 백그라운드에서도 실행
    // refetchIntervalInBackground: true,
    // // 탭이 포커스될 때 자동 갱신
    // refetchOnWindowFocus: true,
  });

  const fetchNotifications = useCallback(
    async ({ pageParam = 1 }): Promise<NotificationPage> => {
      const LIMIT = 10;
      const response = await apiGetNotifications(
        {
          page: pageParam,
          limit: LIMIT,
        },
        user.token,
      );

      console.log("fetchNotifications", response);

      return {
        notifications: response.notifications,
        nextPage: pageParam + 1,
        hasMore: response.notifications.length === LIMIT,
      };
    },
    [user?.token],
  );

  const {
    data,
    //@ts-ignore
    error,
    //@ts-ignore
    status, // 'pending', 'error', 'success'
    fetchNextPage, // 다음 페이지를 가져오는 함수
    hasNextPage, // getNextPageParam이 null 또는 undefined가 아닌 값을 반환하는 경우 true.
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["notifications", user?._id],
    queryFn: fetchNotifications,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextPage : undefined,
    enabled: !!user,
  });

  const allNotifications =
    data?.pages.flatMap((page) => page.notifications) ?? [];

  const handleReadNotification = useCallback(
    async (notificationId: number) => {
      console.log("NotificationButton read notification.", notificationId);
      // 캐시된 데이터 수동 업데이트
      queryClient.setQueryData<NotificationCount>(
        ["notification_count", user?._id],
        (oldData: any) => {
          return {
            total: oldData.total,
            unread: Math.max(0, oldData.unread - 1),
          };
        },
      );
      queryClient.setQueryData<NotificationData>(
        ["notifications", user?._id],
        (oldData) => {
          if (!oldData) return oldData;

          const newPages = oldData.pages.map((page) => {
            // 이 페이지에 타겟 알림이 없다면
            if (!page.notifications.some((n) => n.id === notificationId)) {
              return page; // 기존 페이지 참조 반환(최적화)
            }
            // 타겟 알림이 있다면 업데이트
            return {
              ...page,
              notifications: page.notifications.map((notification) => {
                if (notification.id === notificationId) {
                  return {
                    ...notification,
                    isRead: true,
                  };
                }
                return notification;
              }),
            };
          });

          return {
            ...oldData,
            pages: newPages,
          };
        },
      );
    },
    [queryClient, user?._id],
  );

  const markAsRead = useCallback(
    async (notification: any) => {
      // 서버에 저장된 알림이 아니면 무시.
      if (!notification.id) return;
      // 이미 읽은 알림이면 무시.
      if (notification.isRead) return;

      try {
        setIsLoading(true);
        await apiReadNotification(
          { notificationId: notification.id },
          user.token,
        );
        // 알림 목록 갱신(fix: 캐시 데이터에 수동 업데이트하도록 수정.)
        // await refetch();
        await handleReadNotification(notification.id);
      } catch (error) {
        console.error("markAsRead Error:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [user?.token, handleReadNotification],
  );

  const handleClickNotification = useCallback(
    async (notification: any) => {
      console.log("NotificationButton click notification.", notification);
      markAsRead(notification);
      const data =
        typeof notification.data === "string"
          ? JSON.parse(notification.data)
          : notification.data;
      switch (notification.channelId) {
        case "FRIEND_REQUEST":
        case "FRIEND_CANCEL":
          setMainMenu(MAIN_MENUS.FRIENDS);
          setFrendTab(FRIEND_TABS.ReceivedRequests);
          break;
        case "FRIEND_ACCEPT":
        case "FRIEND_REJECT":
          setMainMenu(MAIN_MENUS.FRIENDS);
          setFrendTab(FRIEND_TABS.SentRequests);
          break;
        case "FRIEND_UPDATE":
          // do nothing.
          break;
        case "FRIEND_DELETE":
          setMainMenu(MAIN_MENUS.FRIENDS);
          break;
        case "CHAT_CREATE":
        case "CHAT_UPDATE":
        case "CHAT_LEAVE":
          enterChat(data.chatId);
          break;
        case "CHAT_MESSAGE":
          enterChat(data.chat._id);
          break;
        case "CHAT_DELETE":
          setMainMenu(MAIN_MENUS.CHATS);
          break;
        default:
          console.error(
            "Unknown notification channelId:",
            notification.channelId,
          );
          break;
      }
    },
    [markAsRead, enterChat],
  );

  const handleNewNotification = useCallback(
    async (newNotification: any) => {
      console.log("NotificationButton get notification.", newNotification);

      //==============================
      // 클릭 이벤트 처리.
      //==============================
      if (newNotification.isClicked) {
        handleClickNotification(newNotification);
        return;
      }

      //==============================
      // 업데이트 이벤트 처리.
      //==============================
      // 알림 보관 기능 없이 푸시 알림만 오는 메시지들.
      if (
        newNotification.channelId === "CHAT_MESSAGE" ||
        newNotification.channelId === "CHAT_WHISPER"
      ) {
        // 업데이트는 하지 않지만 클릭 이벤트는 처리한다.
        return;
      }

      // 캐시 데이터 수동 업데이트
      queryClient.setQueryData<NotificationCount>(
        ["notification_count", user?._id],
        (oldData: any) => {
          return {
            total: oldData.total + 1,
            unread: oldData.unread + (newNotification.isRead ? 0 : 1),
          };
        },
      );
      queryClient.setQueryData<NotificationData>(
        ["notifications", user?._id],
        (oldData) => {
          if (!oldData) return oldData;

          // 첫 페이지에 새 알림 추가
          const newPages = [...oldData.pages];
          newPages[0] = {
            ...newPages[0],
            notifications: [newNotification, ...newPages[0].notifications],
          };

          return {
            ...oldData,
            pages: newPages,
          };
        },
      );
    },
    [queryClient, user?._id, handleClickNotification],
  );

  const NOTIFICATION_EVENTS = [...FRIEND_EVENTS, ...CHAT_EVENTS] as const;
  useEffect(() => {
    // Register all handlers
    NOTIFICATION_EVENTS.forEach((event) => {
      serviceworkerMessageQueue.registerHandler(event, handleNewNotification);
    });
    // Cleanup function
    return () => {
      NOTIFICATION_EVENTS.forEach((event) => {
        serviceworkerMessageQueue.unregisterHandler(event);
      });
    };
  }, [handleNewNotification]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-4 w-4" />
          {count.unread > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 dark:bg-red-500 text-white text-xs flex items-center justify-center">
              {count.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0">
        <Card className="relative bg-background text-foreground border-none shadow-lg">
          {(isLoading || isFetching || isFetchingCount) && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-lg z-10">
              <RotateCw className="h-6 w-6 animate-spin text-foreground" />
            </div>
          )}
          <div className="w-full max-h-[300px] overflow-auto p-2">
            {allNotifications.length === 0 ? (
              <p className="text-center text-muted-foreground">
                {t("NotificationZero")}
              </p>
            ) : (
              <InfiniteScroll
                items={allNotifications}
                itemRender={(index, notification) => {
                  return (
                    <NotificationMessage
                      key={notification.id || index}
                      notification={notification}
                      onClickMessage={handleClickNotification}
                      locale={language}
                    />
                  );
                }}
                loadMore={fetchNextPage}
                hasMore={!!hasNextPage}
                isLoading={isFetchingNextPage}
                reverseOrder={false}
              />
            )}
          </div>
        </Card>
      </PopoverContent>
    </Popover>
  );
}
